// RING GEN — the far ring's texture, painted ON THE DEVICE (gs-ring-genome
// D7): the gensched pass 'ring' (gensched.js GenPass) paints one sector at
// one level per job from the bake's three per-sector inputs
// (meta.panorama.classes: the class raster, the light inputs, the 64 m
// residual — pipeline/bake.py bake_ring_semantic) + the world's genome, over
// the device's OWN height raster of the ground tree's outer nodes under the
// sector (H1; ring.js relief: GM03 bytes, groundm/codec.js) INTO the sector's
// entry of the ONE far atlas (faratlas.js, w38's terrain.farAtlas). The
// painter is ringref.js's, line by line (its CPU reference: the gate is
// scripts/check-ring.mjs R1-R5 on ringref, R9 --gpu = window.__vr.ringCheck
// on this); its GLSL is groundglsl.js's exported genome chunks (genomeGLSL,
// GLSL_CHUNKS, genomeRecRows — never re-typed here; genomeT's octave frames
// are the record rows', so NOISE's fixed frames stay unset).
//
// installRingGen(gen, ring, meta, baseUrl, getRenderer): ring.js
// buildRingTex registers the pass once per ring (ring = its { atlas, relief,
// ready, commit, fallback, sectors }); getRenderer() -> the THREE.WebGLRenderer
// the atlas lives on. The atlas calls (faratlas.js): alloc(id, px, level) ->
// entry (null: no room — transient, gensched's backoff), target(entry) ->
// { framebuffer, viewport = the entry's inner rect } painted at level 0 only
// (never generateMipmap: commit fills the pad and the entry's mips),
// free(id, true) for an abandoned alloc, commit through ring.commit (a late
// coarser level is dropped there). THROWS without getRenderer / an atlas, on
// a bake without panorama.classes or without surface.genome (the rest of
// ringref.ringWorld's contract: at the first job). FAILURES (gensched
// FAILURES): a raster of the wrong size or sha, a class byte outside
// classes.paint, a broken ringWorld contract, no EXT_color_buffer_float / a
// shader that does not compile, no relief node (or no relief triangle) under
// a ready sector are DETERMINISTIC (ringref ringFault): the sector is
// given up for this bake (G.bad: every level at once), console.error once,
// and DRAWS flat (pass.fail -> ring.fallback, ringref ringFallbackRGB); a
// failed fetch / a run without a renderer is transient (gensched backoff).
// EXACT DATA (owner ruling 2026-10-25): the class and residual rasters never
// pass through a canvas (a browser's canvas farbling — Brave — flips their
// low bits on readback: a land id turns into another, water 254 into 255):
// raw u8 files, sha-checked (exactSector, inside the EXACT-DATA markers the
// gate scripts/check-ring.mjs exact:no-canvas scans).
//
// THE PASS { id 'ring', params [] (nothing live: gen.up is a ringtiers.json
// constant), pool null (the atlas owns every texel: evict is never called),
// key id@level, need = the eye's distance to the sector rect
// (ringtiers rectDist, nearest first; boot = 0), lead = tier 2 (the far
// silhouette first), inputs = cls, lit, res + the genome (ringref
// ringInputs: level-independent, one fetch per sector), prep = OFF the frame
// on the fetched bytes (at most gensched PREP_AHEAD sectors decoded ahead): cls / res raw u8 checked against their sha16 (ringref
// ringExact), lit (lossy WebP, w x 2h) an ImageBitmap of its size (no colour
// conversion, no premultiply), the genome doc + the world (ringWorld) once,
// and the sector's genome records not cached yet SOLVED IN THE CLIENT WORKER
// POOL (R8c5: pool-genome.js genome.params = genometex genomeTexParams, ~140
// ms a record, one job per record shared by every sector asking it, prio
// 'bulk' as the ring's fetches; landed by ringref ringSetRecord) — the job
// runs only once every record it paints is cached (a failed solve fails the
// prep: logged, retried with gensched's backoff, nothing drawn), ready = the player
// stands (ringtiers ringLatched over sched phase / hidden: never before the
// latch while the overlay is up) and ring.ready (every relief node of the
// rect fetched or given up), fail = ring.fallback }. run steps, each its own
// run ('yield' between GPU stages: one stage a frame):
//   decode   the class bytes checked against classes.paint, the sector's
//            genome records (solved by prep: never on the main thread)
//   link     the four programs issued once per world, then 'yield' until
//            COMPLETION_STATUS says linked (R8d6c: no status wait on the frame)
//   upload   the atlas entry (atlas.alloc(id, px, level)), cls R8UI, lit R8
//            (w x 2h, the bitmap's red: texSubImage2D, no readback), res
//            RGBA8, uRec RGBA32F (genomeRecRows at the texel, origin
//            floor(rect x0, z0)); uGrad / uGz once per world
//   relief   one relief node a run: decoded (groundm/codec.js decodeNode,
//            meshOf), its vertices in metres off the rect's x0 / z0 (Float32)
//            + Uint32 indices uploaded; the y range kept
//   raster   the relief nodes top-down into RG32F (h, covered): row 0 =
//            north, depth = -height; the levels coarse -> fine with the
//            depth cleared between them (the FINEST level wins, within one
//            the HIGHEST triangle: ringref rasterHeights)
//   fill     uncovered texels: the nearest covered of the row (left first),
//            then the column (two passes, rasterHeights' rule)
//   paint    row bands of PAINT_TEXELS into the entry's inner rect of the
//            atlas's level 0 (atlas.target: GL row 0 = south: v 1 = north)
//   commit   ring.commit(entry) (atlas.commit: pad + mips + swap; a late
//            coarser level dropped) -> done
// A job gensched dropped (no callback) is freed when the next one runs.
//
// THE QUICK PAINT (far-field-load, APP_HORIZON.md §5): at install, before the
// latch and before any relief node, every sector gets a FIRST colour from the
// world's data: its cls + res (the ring lane, through the beam pause; exact,
// sha-checked: exactSector) + the genome doc, painted at its COARSEST level
// (ringtiers genLevels - 1) by ringref ringPaint {noTex, noVis} over
// ringref quickHeights of the core's sampler (ring.held) in the client
// worker pool (pool-ring.js ring.quick, prio 'bulk'), uploaded into a
// provisional atlas entry (atlas.alloc(.., quick) -> upload -> ring.quick:
// atlas.commit(entry, mean rgb)). The far tree draws it at once (the sector
// covered: the outer nodes too); the pass's painted levels replace it as they
// land (the sector's level stays -1). Skipped for a sector the pass already
// painted or holds an alloc of; a failed fetch / check / paint / no room
// leaves the sector to the pass (logged, never retried).
import { genomeGLSL, GLSL_CHUNKS, genomeRecRows } from './groundglsl.js';
import { ringWorld, ringSetRecord, ringInputs, ringPaint, rasterHeights, gmChunks, ringFault, ringExact, ringFallbackRGB, quickHeights, WATER_ID } from './ringref.js';
import { rectDist, ringLatched, genLevels, RING_CLASSES_FORMAT } from './ringtiers.js';
import { phase, hidden } from './sched.js';
import { transportUrl, fetchAsset } from './assetq.js';
import { run as poolRun } from './pool.js';
import CJ from '../../pipeline/classes.json';
import PAL from './painterpal.json';

const PAINT_TEXELS = 65536;          // a paint band (texels a run)
const NSLOT = 17;                    // class slots: ids 0..15, water (254) -> 16
const slotOf = (cid) => (cid === WATER_ID ? 16 : cid < 16 ? cid : -1);

const { HEAD, RASTER, NOISE } = GLSL_CHUNKS;
const VS_FULL = HEAD + `void main() {
  gl_Position = vec4(float((gl_VertexID & 1) << 2) - 1.0, float((gl_VertexID & 2) << 1) - 1.0, 0.0, 1.0);
}
`;
// the relief vertices (m: x, z off the rect's x0, z0; y the ground's) -> the
// raster: x right, z up the target (row 0 = north = z0), depth = -height
const VS_RASTER = HEAD + `
in vec3 aP;
uniform vec2 uSz;      // the rect's width, height (m)
uniform vec2 uY;       // the height range: y0, 1 / (y1 - y0)
out float vY;
void main() {
  vec2 p = aP.xz;
  vY = aP.y;
  gl_Position = vec4(p / uSz * 2.0 - 1.0, clamp(1.0 - 2.0 * (vY - uY.x) * uY.y, -1.0, 1.0), 1.0);
}
`;
const FS_RASTER = HEAD + `
in float vY;
out vec4 o;
void main() { o = vec4(vY, 1.0, 0.0, 0.0); }
`;
// fill 1: an uncovered texel <- the nearest covered of its row to the left,
// else the row's first; g = 1 where the row has any covered texel
const FS_ROW = HEAD + `
uniform sampler2D uS;
uniform ivec2 uN;
out vec4 o;
void main() {
  ivec2 q = ivec2(gl_FragCoord.xy);
  vec2 s = texelFetch(uS, q, 0).rg;
  if (s.g > 0.5) { o = vec4(s.r, 1.0, 0.0, 0.0); return; }
  for (int i = q.x - 1; i >= 0; i--) { vec2 t = texelFetch(uS, ivec2(i, q.y), 0).rg; if (t.g > 0.5) { o = vec4(t.r, 1.0, 0.0, 0.0); return; } }
  for (int i = q.x + 1; i < uN.x; i++) { vec2 t = texelFetch(uS, ivec2(i, q.y), 0).rg; if (t.g > 0.5) { o = vec4(t.r, 1.0, 0.0, 0.0); return; } }
  o = vec4(0.0);
}
`;
// fill 2: a row with no covered texel <- the first covered row if it lies
// north of it, else the last
const FS_COL = HEAD + `
uniform sampler2D uS;
uniform ivec2 uN;
out vec4 o;
void main() {
  ivec2 q = ivec2(gl_FragCoord.xy);
  vec2 s = texelFetch(uS, q, 0).rg;
  if (s.g > 0.5) { o = vec4(s.r, 1.0, 0.0, 0.0); return; }
  int ka = -1, kb = -1;
  for (int k = 0; k < uN.y; k++) if (texelFetch(uS, ivec2(0, k), 0).g > 0.5) { if (ka < 0) ka = k; kb = k; }
  o = vec4(texelFetch(uS, ivec2(q.x, q.y < ka ? ka : kb), 0).r, 1.0, 0.0, 0.0);
}
`;
// the painter (ringref ringPaint per texel)
const FS_PAINT = () => HEAD + RASTER + NOISE + genomeGLSL() + `
uniform sampler2D uHt;        // RG32F heights (the relief's flat-earth y), row 0 = north
uniform usampler2D uCl;       // R8UI class raster, row 0 = north
uniform sampler2D uLt;        // R8 light (sun rows over sky rows)
uniform sampler2D uRs;        // RGBA8 residual codes
uniform ivec2 uCP;            // the class raster (pw, ph)
uniform float uCell;          // the class cell (m)
uniform vec2 uTz;             // texel x, z (m)
uniform vec2 uOff;            // the rect's x0, z0 - (X0, Z0)
uniform vec2 uROff;           // the residual rect's x0, z0 - (X0, Z0)
uniform ivec2 uRP;            // the residual raster (rw, rh)
uniform float uRC;            // the residual cell (m)
uniform float uPerStop;
uniform ivec2 uVp;            // the atlas entry's inner rect origin (texels)
uniform int uMode[${NSLOT}];  // 0 a genome record, 1 a palette colour, -1 none
uniform int uRow[${NSLOT}];
uniform vec3 uPal[${NSLOT}];
uniform float uAnchor;
uniform vec3 uSunL;           // (sx, sy, sz)
uniform vec3 uLSky, uLGround, uLSun;
uniform float uWhite;
out vec4 o;
float bilR(sampler2D t, int w, int h, int ch, vec2 uv, int r0) {
  vec2 f = floor(uv), tt = clamp(uv - f, 0.0, 1.0);
  int i0 = clamp(int(f.x), 0, w - 1), i1 = clamp(int(f.x) + 1, 0, w - 1);
  int j0 = clamp(int(f.y), 0, h - 1) + r0, j1 = clamp(int(f.y) + 1, 0, h - 1) + r0;
  float a = rnd(texelFetch(t, ivec2(i0, j0), 0)[ch] * 255.0), b = rnd(texelFetch(t, ivec2(i1, j0), 0)[ch] * 255.0);
  float c = rnd(texelFetch(t, ivec2(i0, j1), 0)[ch] * 255.0), d = rnd(texelFetch(t, ivec2(i1, j1), 0)[ch] * 255.0);
  return (a * (1.0 - tt.x) + b * tt.x) * (1.0 - tt.y) + (c * (1.0 - tt.x) + d * tt.x) * tt.y;
}
float srgbEnc(float c) { c = clamp(c, 0.0, 1.0); return c <= 0.0031308 ? c * 12.92 : 1.055 * pow(c, 1.0 / 2.4) - 0.055; }
float srgbDec(float c) { return c <= 0.04045 ? c / 12.92 : pow((c + 0.055) / 1.055, 2.4); }
float to8(float x) { return floor(srgbEnc(x) * 255.0 + 0.5); }
// the relief's height as it stands (flat-earth truth: no earth drop re-added)
float hAt(int i, int k) { return texelFetch(uHt, ivec2(i, k), 0).r; }
void main() {
  ivec2 fc = ivec2(gl_FragCoord.xy) - uVp;
  int i = fc.x, k = uPW.y - 1 - fc.y;
  vec2 d = uOff + (vec2(i, k) + 0.5) * uTz;
  int il = max(i - 1, 0), ir = min(i + 1, uPW.x - 1), ku = max(k - 1, 0), kd = min(k + 1, uPW.y - 1);
  float h = hAt(i, k);
  float gx = (hAt(ir, k) - hAt(il, k)) / (float(ir - il) * uTz.x);
  float gz = (hAt(i, kd) - hAt(i, ku)) / (float(kd - ku) * uTz.y);
  float sdeg = atan(sqrt(gx * gx + gz * gz)) * (180.0 / PI);
  ivec2 cc = clamp(ivec2(floor((d - uOff) / uCell)), ivec2(0), uCP - 1);
  int cid = int(texelFetch(uCl, cc, 0).r), s = cid == ${WATER_ID} ? 16 : cid;
  vec2 ru = (d - uROff) / uRC - 0.5;
  vec3 R;
  for (int c = 0; c < 3; c++) R[c] = exp2((bilR(uRs, uRP.x, uRP.y, c, ru, 0) - 128.0) / uPerStop);
  vec3 col = s < ${NSLOT} && uMode[s] == 0 ? genomeT(uRow[s], d) * classMeanR(uRow[s], h, sdeg) * R * uAnchor
    : uPal[min(s, ${NSLOT - 1})] * R;
  float lam, ny;
  if (cid == ${WATER_ID}) { lam = uSunL.z; ny = 1.0; } else {
    float n = sqrt(gx * gx + gz * gz + 1.0);
    lam = clamp((gx * -uSunL.x + gz * uSunL.y + uSunL.z) / n, 0.0, 1.0);
    ny = 1.0 / n;
  }
  vec2 lu = (d - uOff) / uCell - 0.5;
  float vis = bilR(uLt, uCP.x, uCP.y, 0, lu, 0) / 255.0, sky = bilR(uLt, uCP.x, uCP.y, 0, lu, uCP.y) / 255.0;
  float w = 0.5 * ny + 0.5;
  vec3 E = clamp(((w * (uLSky - uLGround) + uLGround) * sky + lam * vis * uLSun) / uWhite, 0.0, 1.0);
  vec3 lit;
  for (int c = 0; c < 3; c++) {
    float a = srgbEnc(col[c]) * 255.0;   // the albedo (sRGB 0..255, unquantised)
    lit[c] = srgbDec(to8(srgbDec(a / 255.0) * srgbDec(to8(E[c]) / 255.0)) / 255.0);
  }
  o = vec4(lit, 1.0);        // SRGB8_ALPHA8: stored as the 8-bit lit
}
`;

// ---------------------------------------------------------------- GL helpers
// R8d6c THE LINK WITHOUT A WAIT: compile + link are only issued here (no
// status read: getShaderParameter COMPILE_STATUS / getProgramParameter
// LINK_STATUS right after them waited for the driver — one 322-359 ms task in
// quiet windows 3 and 4); programLinked asks COMPLETION_STATUS
// (KHR_parallel_shader_compile, never waits) once a run until every program
// is done, then LINK_STATUS — a failed compile is a failed link, its logs the
// shaders' (ringFault). Without the extension the first ask is the link wait,
// as before. Exported for scripts/check-compileq.mjs 13.
export function program(gl, vs, fs) {
  const p = gl.createProgram();
  for (const [t, src] of [[gl.VERTEX_SHADER, vs], [gl.FRAGMENT_SHADER, fs]]) {
    const s = gl.createShader(t);
    gl.shaderSource(s, src);
    gl.compileShader(s);
    gl.attachShader(p, s);
  }
  gl.bindAttribLocation(p, 0, 'aP');
  gl.linkProgram(p);
  const U = new Map();
  const u = (n) => { if (!U.has(n)) U.set(n, gl.getUniformLocation(p, n)); return U.get(n); };
  return { p, u };
}
// progs: { name: program() } -> true once every one is linked (throws a
// ringFault on a failed one), false while one still links (par: the
// extension object, or null)
export function programLinked(gl, par, progs) {
  const all = Object.values(progs);
  if (par && !all.every((P) => gl.getProgramParameter(P.p, par.COMPLETION_STATUS_KHR))) return false;
  for (const P of all) {
    if (!gl.getProgramParameter(P.p, gl.LINK_STATUS)) {
      const logs = (gl.getAttachedShaders(P.p) || []).map((s) => gl.getShaderInfoLog(s)).filter(Boolean).join('\n');
      throw ringFault(`ringgen program: ${gl.getProgramInfoLog(P.p)}\n${logs}`);
    }
  }
  return true;
}
function rawTex(gl, ifmt, fmt, type, w, h, data) {
  const t = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D, t);
  gl.texStorage2D(gl.TEXTURE_2D, 1, ifmt, w, h);
  for (const [k, v] of [[gl.TEXTURE_MIN_FILTER, gl.NEAREST], [gl.TEXTURE_MAG_FILTER, gl.NEAREST],
    [gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE], [gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE]]) gl.texParameteri(gl.TEXTURE_2D, k, v);
  if (data) {
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, w, h, fmt, type, data);
  }
  return t;
}
// EXACT-DATA-BEGIN (scripts/check-ring.mjs exact:no-canvas: no canvas, bitmap or readback in here)
// the class + residual rasters -> ringref's sector shape (lit apart): raw u8 (.cls.bin w x h,
// .res.bin RGB rw x rh, row 0 = north) — the fetched bytes ARE the decoded bytes, checked once
// against the bake's sha16 (ringExact: a wrong size / sha is a ringFault)
async function exactSector(q, inputs) {
  const [pw, ph] = q.px, [rw, rh] = q.res.px;
  const [cls, codes] = await Promise.all([ringExact(inputs[0], q.cls, pw * ph, `ringgen: ${q.cls.file}`),
    ringExact(inputs[2], q.res, rw * rh * 3, `ringgen: ${q.res.file}`)]);
  return { tier: q.tier, rect: q.rect, px: q.px, cls, res: { codes, px: q.res.px, rect: q.res.rect } };
}
// EXACT-DATA-END
// the light inputs (lossy WebP, w x 2h: colour data, not exact): a bitmap uploaded straight to GL
async function litBitmap(q, ab) {
  const bm = await createImageBitmap(new Blob([ab], { type: 'image/webp' }), { colorSpaceConversion: 'none', premultiplyAlpha: 'none' });
  const [pw, ph] = q.px, w = bm.width, h = bm.height;
  if (w !== pw || h !== 2 * ph) { bm.close(); throw ringFault(`ringgen: ${q.lit.file} ${w}x${h}, want ${pw}x${2 * ph}`); }
  return bm;
}
// (R9 only, the CPU reference's lit bytes: a canvas readback of the LOSSY light inputs — never
// on the play path, never exact data)
async function litBytes(q, ab) {
  const bm = await litBitmap(q, ab), w = bm.width, h = bm.height;
  const cx = new OffscreenCanvas(w, h).getContext('2d', { willReadFrequently: true });
  cx.drawImage(bm, 0, 0); bm.close();
  const d = cx.getImageData(0, 0, w, h).data, a = new Uint8Array(w * h);
  for (let k = 0; k < w * h; k++) a[k] = d[4 * k];
  return a;
}

// ---------------------------------------------------------------- the pass
export function installRingGen(gen, ring, meta, baseUrl, getRenderer) {
  const C = meta.panorama.classes, A = ring.atlas;
  if (typeof getRenderer !== 'function') throw new Error('ringgen: no getRenderer (the THREE.WebGLRenderer the atlas lives on)');
  if (!A) throw new Error('ringgen: no far atlas (terrain.farAtlas)');
  if (!(meta.surface && meta.surface.genome && meta.surface.genome.file)) throw new Error('ringgen: meta.surface.genome missing');
  const genomeUrl = `${baseUrl}/${meta.surface.genome.file}`;
  if (!(C && C.format === RING_CLASSES_FORMAT)) throw new Error(`ringgen: meta.panorama.classes is not ${RING_CLASSES_FORMAT}`);
  // the world (ringref.ringWorld: the rest of the contract, THROWS) at the first prep, with the genome
  // bad: sector id -> its deterministic fault (FAILURES: every level given up at once); doc: the genome
  // solving: genome record key -> the promise of its pool solve landing (one job per record)
  // prog / par / linked: the four programs, KHR_parallel_shader_compile, all four seen linked (R8d6c)
  // pend: sector id -> its latest alloc (an abandoned job frees only its own)
  const G = { world: null, doc: null, prog: null, par: null, linked: false, vao: null, tGrad: null, tGz: null, live: null, done: [], bad: new Map(), rgb: null, solving: new Map(),
    pend: new Map() };

  // the world (ringref.ringWorld: the rest of the contract) once, over the genome doc; a broken contract is a ringFault
  function world() {
    if (!G.world) {
      try { G.world = ringWorld(meta, G.doc, PAL, CJ); } catch (e) { throw ringFault(`ringgen: ${e && e.message}`); }
    }
    return G.world;
  }
  // (R8c5) the genome records a sector paints (its class bytes' records; none: the genome's first —
  // worldTex's gz is any record's) not cached yet, solved in the pool (genome.params) and landed in
  // the world (ringref ringSetRecord) -> resolves once all of them are cached; a record already asked
  // shares its job. A failed solve rejects (the prep's failure: transient, gensched's backoff).
  function solve(cls) {
    const W = world(), keys = new Set();
    for (const cid of new Set(cls)) { const k = W.recOf.get(cid); if (k) keys.add(k); }
    if (!keys.size) keys.add(Object.keys(W.genome.classes)[0]);
    return Promise.all([...keys].filter((k) => !W.gP.has(k)).map((k) => {
      let p = G.solving.get(k);
      if (!p) {
        p = poolRun('genome.params', { rec: W.genome.classes[k], key: k, table: W.gtTable }, { prio: 'bulk' })
          .then((P) => { ringSetRecord(W, k, P); }, (e) => { console.warn(`gen.ring genome ${k}: ${e && e.message}`); throw e; })
          .finally(() => { G.solving.delete(k); });
        G.solving.set(k, p);
      }
      return p;
    }));
  }

  function free(gl, S) {
    for (const t of S.raw || []) gl.deleteTexture(t);
    for (const b of S.bufs || []) gl.deleteBuffer(b);
    if (S.fbo) gl.deleteFramebuffer(S.fbo);
    if (S.depth) gl.deleteRenderbuffer(S.depth);
    S.raw = []; S.bufs = []; S.fbo = null; S.depth = null; S.nodes = null;
    // an alloc never committed: its pending entry back (only this job's: a newer alloc replaced it)
    if (S.entry && G.pend.get(S.entry.id) === S.entry) { G.pend.delete(S.entry.id); A.free(S.entry.id, true); }
    S.entry = null;
  }
  function programs(gl, R) {
    if (G.prog) return G.prog;
    if (!R.extensions.has('EXT_color_buffer_float')) throw ringFault('ringgen: no EXT_color_buffer_float (RG32F height raster)');
    G.par = R.extensions.get('KHR_parallel_shader_compile');
    G.vao = gl.createVertexArray();
    G.prog = { raster: program(gl, VS_RASTER, FS_RASTER), row: program(gl, VS_FULL, FS_ROW), col: program(gl, VS_FULL, FS_COL),
      paint: program(gl, VS_FULL, FS_PAINT()) };
    return G.prog;
  }
  function worldTex(gl) {
    if (G.tGrad) return;
    const gr = new Float32Array(512);
    for (let k = 0; k < 256; k++) { const a = (k + 0.5) * ((2 * Math.PI) / 256); gr[2 * k] = Math.fround(Math.cos(a)); gr[2 * k + 1] = Math.fround(Math.sin(a)); }
    G.tGrad = rawTex(gl, gl.RG32F, gl.RG, gl.FLOAT, 256, 1, gr);
    // the table's curve, the same in every record: any solved one (prep solved at least one)
    const P0 = G.world.gP.values().next().value;
    if (!P0) throw new Error('ringgen: no genome record solved (prep)');
    const gz = P0.gz;
    G.tGz = rawTex(gl, gl.R32F, gl.RED, gl.FLOAT, gz.length, 1, Float32Array.from(gz));
  }
  const bindTex = (gl, P, name, unit, tex) => { gl.activeTexture(gl.TEXTURE0 + unit); gl.bindTexture(gl.TEXTURE_2D, tex); gl.uniform1i(P.u(name), unit); };
  const target = (gl, S, tex) => {
    gl.bindFramebuffer(gl.FRAMEBUFFER, S.fbo);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
  };

  function step(gl, job, R) {
    const S = job.state, t = job.tile, q = C.sectors[t.id];
    switch (S.step || 'decode') {
      case 'decode': {
        // (the inputs are decoded + sha-checked and the genome records solved already: pass.prep, off the frame)
        world();
        S.sec = job.inputs.sec;
        const ids = new Set(S.sec.cls);
        S.keys = [];
        for (const cid of ids) {
          if (slotOf(cid) < 0 || !(String(cid) in C.paint)) throw ringFault(`ringgen: ${t.id} class byte ${cid} is not in classes.paint`);
          const k = G.world.recOf.get(cid);
          if (k && !S.keys.includes(k)) S.keys.push(k);
        }
        S.keys.sort();
        S.ids = ids;
        // every record solved by prep (the pool) — never solved here
        const k = S.keys.find((key) => !G.world.gP.has(key));
        if (k) throw new Error(`ringgen: ${t.id} genome record ${k} not solved (prep)`);
        S.step = 'link';
        return 'more';
      }
      case 'link': {
        // the programs issued once per world, then one COMPLETION_STATUS ask a
        // frame ('yield') until they are linked (R8d6c)
        const P = programs(gl, R);
        if (!G.linked && !(G.linked = programLinked(gl, G.par, P))) return 'yield';
        S.step = 'upload';
        return 'more';
      }
      case 'upload': {
        worldTex(gl);
        const sc = S.sec, [pw, ph] = sc.px, w = t.px[0], h = t.px[1];
        // the atlas entry first (no room: transient — gensched's backoff, nothing else made)
        const e = A.alloc(t.id, sc.px, t.level);
        if (!e) throw new Error(`ringgen: no far atlas room for ${t.id} L${t.level}`);
        G.pend.set(t.id, e);
        S.entry = e;
        if (e.px[0] !== w || e.px[1] !== h) throw ringFault(`ringgen: atlas entry ${e.px} for ${t.id} L${t.level}, want ${w} x ${h}`);
        S.w = w; S.h = h; S.tx = C.cell_m[String(sc.tier)] * pw / w; S.tz = C.cell_m[String(sc.tier)] * ph / h;
        S.X0 = Math.floor(sc.rect[0]); S.Z0 = Math.floor(sc.rect[1]);
        S.raw = []; S.bufs = [];
        // the inputs
        S.raw.push(S.glCls = rawTex(gl, gl.R8UI, gl.RED_INTEGER, gl.UNSIGNED_BYTE, pw, ph, sc.cls));
        // lit: the bitmap's red channel, uploaded as decoded (no flip, no premultiply, no conversion)
        S.raw.push(S.tLit = rawTex(gl, gl.R8, gl.RED, gl.UNSIGNED_BYTE, pw, 2 * ph, null));
        gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
        gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
        gl.pixelStorei(gl.UNPACK_COLORSPACE_CONVERSION_WEBGL, gl.NONE);
        gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, pw, 2 * ph, gl.RED, gl.UNSIGNED_BYTE, sc.lit);
        const [rw, rh] = sc.res.px, rgba = new Uint8Array(rw * rh * 4);
        for (let k = 0; k < rw * rh; k++) { rgba[4 * k] = sc.res.codes[3 * k]; rgba[4 * k + 1] = sc.res.codes[3 * k + 1]; rgba[4 * k + 2] = sc.res.codes[3 * k + 2]; rgba[4 * k + 3] = 255; }
        S.raw.push(S.tRes = rawTex(gl, gl.RGBA8, gl.RGBA, gl.UNSIGNED_BYTE, rw, rh, rgba));
        const nk = Math.max(1, S.keys.length), rec = new Float32Array(48 * 4 * nk);
        if (S.keys.length) genomeRecRows(rec, S.keys, G.world.gP, new Map(), G.world.genome.classes, S.tx, S.X0, S.Z0);
        S.raw.push(S.tRec = rawTex(gl, gl.RGBA32F, gl.RGBA, gl.FLOAT, 48, nk, rec));
        // the height ping-pong + its depth
        S.raw.push(S.hA = rawTex(gl, gl.RG32F, gl.RG, gl.FLOAT, w, h, null), S.hB = rawTex(gl, gl.RG32F, gl.RG, gl.FLOAT, w, h, null));
        S.fbo = gl.createFramebuffer();
        S.depth = gl.createRenderbuffer();
        gl.bindRenderbuffer(gl.RENDERBUFFER, S.depth);
        gl.renderbufferStorage(gl.RENDERBUFFER, gl.DEPTH_COMPONENT24, w, h);
        // the relief: coarse -> fine (the raster's order)
        S.nodes = ring.relief(t.id).sort((a, b) => b.k - a.k);
        if (!S.nodes.length) throw ringFault(`ringgen: no relief node under ${t.id}`);
        S.ni = 0; S.y0 = Infinity; S.y1 = -Infinity; S.parts = [];
        S.step = 'relief';
        return 'yield';
      }
      case 'relief': {
        // one node a run: decoded, its vertices off the rect's x0 / z0 (Float32 m), uploaded
        const sc = S.sec, [c] = gmChunks([S.nodes[S.ni]]), nv = c.x.length, v = new Float32Array(3 * nv);
        for (let k = 0; k < nv; k++) {
          v[3 * k] = c.x[k] - sc.rect[0]; v[3 * k + 1] = c.y[k]; v[3 * k + 2] = c.z[k] - sc.rect[1];
          if (c.y[k] < S.y0) S.y0 = c.y[k];
          if (c.y[k] > S.y1) S.y1 = c.y[k];
        }
        const vb = gl.createBuffer(), ib = gl.createBuffer();
        S.bufs.push(vb, ib);
        gl.bindBuffer(gl.ARRAY_BUFFER, vb);
        gl.bufferData(gl.ARRAY_BUFFER, v, gl.STATIC_DRAW);
        gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ib);
        gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, c.idx, gl.STATIC_DRAW);
        S.parts.push({ vb, ib, n: c.idx.length, k: c.lvl });
        if (++S.ni < S.nodes.length) return 'yield';
        if (!S.parts.some((q) => q.n > 0)) throw ringFault(`ringgen: no relief triangle under ${t.id}`);
        S.step = 'raster';
        return 'yield';
      }
      case 'raster': {
        const P = programs(gl).raster, sc = S.sec;
        target(gl, S, S.hA);
        gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.RENDERBUFFER, S.depth);
        gl.viewport(0, 0, S.w, S.h);
        gl.disable(gl.BLEND); gl.disable(gl.CULL_FACE); gl.disable(gl.SCISSOR_TEST);
        gl.enable(gl.DEPTH_TEST); gl.depthFunc(gl.LESS); gl.depthMask(true);
        gl.clearColor(0, 0, 0, 0); gl.clearDepth(1);
        gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
        gl.useProgram(P.p);
        gl.uniform2f(P.u('uSz'), sc.rect[2] - sc.rect[0], sc.rect[3] - sc.rect[1]);
        gl.uniform2f(P.u('uY'), S.y0 - 1, 1 / (S.y1 - S.y0 + 2));
        const vao = gl.createVertexArray();
        gl.bindVertexArray(vao);
        gl.enableVertexAttribArray(0);
        // coarse -> fine: a finer level overwrites (the depth cleared between levels), within a level the highest
        let lk = null;
        for (const q of S.parts) {
          if (lk !== null && q.k !== lk) gl.clear(gl.DEPTH_BUFFER_BIT);
          lk = q.k;
          gl.bindBuffer(gl.ARRAY_BUFFER, q.vb);
          gl.vertexAttribPointer(0, 3, gl.FLOAT, false, 0, 0);
          gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, q.ib);
          gl.drawElements(gl.TRIANGLES, q.n, gl.UNSIGNED_INT, 0);
        }
        gl.bindVertexArray(null);
        gl.deleteVertexArray(vao);
        for (const b of S.bufs) gl.deleteBuffer(b);
        S.bufs = []; S.parts = []; S.nodes = null;
        gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.RENDERBUFFER, null);
        gl.disable(gl.DEPTH_TEST);
        S.step = 'fill';
        return 'yield';
      }
      case 'fill': {
        const PP = programs(gl);
        gl.bindVertexArray(G.vao);
        gl.viewport(0, 0, S.w, S.h);
        for (const [P, src, dst] of [[PP.row, S.hA, S.hB], [PP.col, S.hB, S.hA]]) {
          target(gl, S, dst);
          gl.useProgram(P.p);
          bindTex(gl, P, 'uS', 0, src);
          gl.uniform2i(P.u('uN'), S.w, S.h);
          gl.drawArrays(gl.TRIANGLES, 0, 3);
        }
        gl.bindVertexArray(null);
        S.step = 'paint'; S.y = 0;
        return 'yield';
      }
      case 'paint': {
        const P = programs(gl).paint, sc = S.sec, W = G.world, L = W.light, s = W.sun;
        // the atlas's level 0, the entry's inner rect (viewport [x, y, w, h])
        const T = A.target(S.entry), [vx, vy] = T.viewport;
        gl.bindFramebuffer(gl.FRAMEBUFFER, T.framebuffer);
        gl.useProgram(P.p);
        gl.bindVertexArray(G.vao);
        bindTex(gl, P, 'uHt', 0, S.hA); bindTex(gl, P, 'uCl', 1, S.glCls); bindTex(gl, P, 'uLt', 2, S.tLit);
        bindTex(gl, P, 'uRs', 3, S.tRes); bindTex(gl, P, 'uRec', 4, S.tRec); bindTex(gl, P, 'uGz', 5, G.tGz);
        bindTex(gl, P, 'uGrad', 6, G.tGrad);
        gl.uniform1f(P.u('uTexel'), S.tx); gl.uniform1i(P.u('uM'), 0);
        gl.uniform2i(P.u('uPW'), S.w, S.h); gl.uniform2i(P.u('uX0'), S.X0, S.Z0); gl.uniform2i(P.u('uVp'), vx, vy);
        gl.uniform2i(P.u('uCP'), sc.px[0], sc.px[1]);
        gl.uniform1f(P.u('uCell'), C.cell_m[String(sc.tier)]);
        gl.uniform2f(P.u('uTz'), S.tx, S.tz);
        gl.uniform2f(P.u('uOff'), sc.rect[0] - S.X0, sc.rect[1] - S.Z0);
        gl.uniform2f(P.u('uROff'), sc.res.rect[0] - S.X0, sc.res.rect[1] - S.Z0);
        gl.uniform2i(P.u('uRP'), sc.res.px[0], sc.res.px[1]);
        gl.uniform1f(P.u('uRC'), C.residual.cell_m);
        gl.uniform1f(P.u('uPerStop'), C.residual.per_stop);
        const mode = new Int32Array(NSLOT).fill(-1), row = new Int32Array(NSLOT), pal = new Float32Array(3 * NSLOT);
        for (const cid of S.ids) {
          const sl = slotOf(cid), k = W.recOf.get(cid);
          if (k) { mode[sl] = 0; row[sl] = S.keys.indexOf(k); } else { mode[sl] = 1; pal.set(W.palLin.get(cid), 3 * sl); }
        }
        gl.uniform1iv(P.u('uMode'), mode); gl.uniform1iv(P.u('uRow'), row); gl.uniform3fv(P.u('uPal'), pal);
        gl.uniform1f(P.u('uAnchor'), W.anchor);
        gl.uniform3f(P.u('uSunL'), s.sx, s.sy, s.sz);
        gl.uniform3fv(P.u('uLSky'), L.sky); gl.uniform3fv(P.u('uLGround'), L.ground); gl.uniform3fv(P.u('uLSun'), L.sun);
        gl.uniform1f(P.u('uWhite'), L.white);
        // one band of rows a run (GL rows from the south)
        const n = Math.max(1, Math.min(S.h - S.y, Math.floor(PAINT_TEXELS / S.w)));
        gl.viewport(vx, vy, S.w, S.h);
        gl.enable(gl.SCISSOR_TEST); gl.scissor(vx, vy + S.y, S.w, n);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
        gl.disable(gl.SCISSOR_TEST);
        gl.bindVertexArray(null);
        S.y += n;
        if (S.y < S.h) return 'yield';
        S.step = 'commit';
        return 'yield';
      }
      case 'commit': {
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        // the pad + the entry's mips + the swap (atlas.commit), or a late coarser level dropped
        G.pend.delete(t.id);
        ring.commit(S.entry);
        S.entry = null;
        free(gl, S);
        return 'done';
      }
      default: throw new Error(`ringgen: step ${S.step}`);
    }
  }

  const pass = {
    id: 'ring', params: [], pool: null,
    key: (t) => `${t.id}@${t.level}`,
    need: (t, view) => (view && view.eye ? rectDist(view.eye.x, view.eye.z, t.rect) : 0),
    lead: (t) => t.tier === 2,
    inputs: (t) => ringInputs(t, genomeUrl),
    // off the frame: the exact rasters (sha-checked), the light bitmap, the genome doc once, the
    // sector's genome records solved in the pool; a sector found bad stays bad for every level (G.bad)
    async prep(t, bufs) {
      if (G.bad.has(t.id)) throw G.bad.get(t.id);
      const q = C.sectors[t.id];
      try {
        if (!G.doc) G.doc = JSON.parse(new TextDecoder().decode(bufs[3]));
        const [sec, lit] = await Promise.all([exactSector(q, bufs), litBitmap(q, bufs[1])]);
        try { await solve(sec.cls); } catch (e) { lit.close(); throw e; }
        return { sec: { ...sec, lit } };
      } catch (e) {
        if (e && e.permanent) G.bad.set(t.id, e);
        throw e;
      }
    },
    ready: (t) => ringLatched(phase(), hidden()) && ring.ready(t.id),
    fail: (t) => ring.fallback(t.id, G.rgb || (G.rgb = ringFallbackRGB(meta, PAL, CJ))),
    step: (job) => job.state.step || '',
    // THE PASS INIT (gensched, while hidden): the four programs issued and, without the parallel
    // extension, their link waited for behind the black — never at the first sector's 'link' in play
    init(gl) {
      const R = getRenderer && getRenderer();
      if (!R || !gl) return;
      try { if (!G.linked) G.linked = programLinked(gl, G.par, programs(gl, R)); } finally { R.resetState(); }
    },
    run(gl, job) {
      const R = getRenderer && getRenderer();
      if (!R) throw new Error('ringgen: no renderer');
      // a job gensched dropped (no callback): its GL objects go now
      if (G.live && G.live !== job) { free(gl, G.live.state); G.live = null; }
      G.live = job;
      try {
        const r = step(gl, job, R);
        if (r === 'done') G.live = null;
        return r;
      } catch (e) {
        free(gl, job.state);
        G.live = null;
        if (e && e.permanent) G.bad.set(job.tile.id, e);
        throw e;
      } finally {
        R.resetState();
      }
    },
    done(job) {
      const t = job.tile;
      job.inputs.sec.lit.close();      // (the light bitmap: uploaded, the job's inputs go with it)
      G.done.push({ id: t.id, level: t.level });
      if (G.done.length > 64) G.done.shift();
    },
  };
  gen.register(pass);

  // THE QUICK PAINT (above): every sector, asked at once (the ring lane ranks the fetches, the pool the paints)
  async function quick(id) {
    const q = C.sectors[id], st = ring.sectors.get(id), done = () => st.level >= 0 || G.pend.has(id) || G.bad.has(id);
    const url = (f) => `${baseUrl}/${C.dir}/${f.file}`;
    const [cb, rb] = await Promise.all([fetchAsset(url(q.cls), { prio: 'bulk' }), fetchAsset(url(q.res), { prio: 'bulk' })]);
    if (!cb || !rb || done()) return;
    const sec = { id, ...(await exactSector(q, [cb, null, rb])) }, W = await quickWorld;
    const level = genLevels(q.px) - 1, w = q.px[0] >> level, h = q.px[1] >> level;
    const heights = quickHeights(ring.held, W, q.rect, w, h);
    const { rgba, rgb } = await poolRun('ring.quick', { world: G.quickW || (G.quickW = { ...W, pano: null, gP: new Map(), gLab: new Map() }), sector: sec, level, heights },
      { transfer: [heights.buffer, sec.cls.buffer, sec.res.codes.buffer], prio: 'bulk' });
    const R = getRenderer();
    if (done() || !R) return;
    try {
      const e = A.alloc(id, q.px, level, true);
      if (!e) return;
      A.upload(e, rgba);
      ring.quick(e, rgb);
    } finally { R.resetState(); }
  }
  // the genome doc (one fetch, shared with the pass's prep) + the world
  const quickWorld = G.doc ? Promise.resolve(world()) : fetchAsset(genomeUrl, { prio: 'bulk', as: 'json' }).then((doc) => {
    if (!G.doc) G.doc = doc;
    return world();
  });
  quickWorld.catch(() => null);
  for (const id of ring.sectors.keys()) quick(id).catch((e) => console.warn(`[ring] quick paint ${id}:`, e && e.message));

  // R9 (scripts/check-ring.mjs --gpu, a browser harness): read back the last
  // n generated sectors (their atlas entries' inner rects) and paint them with
  // ringref from the same inputs (refetched) over the same relief nodes -> {sectors: [{id,
  // level, maxd, over1}], ok: every max |d| per channel <= 1}
  if (typeof window !== 'undefined' && window.__vr) {
    window.__vr.ringCheck = async (n = 2) => {
      const R = getRenderer && getRenderer();
      if (!R || !G.world) throw new Error('ringCheck: nothing generated yet');
      const gl = R.getContext(), out = [];
      for (const { id, level } of G.done.slice(-n)) {
        const st = ring.sectors.get(id), hit = A.lookup(id), e = hit && hit.entry;
        if (!st || st.level !== level || !e || e.level !== level) continue;
        const q = C.sectors[id], w = e.px[0], h = e.px[1];
        const ins = await Promise.all([q.cls, q.lit, q.res].map((f) => fetch(transportUrl(`${baseUrl}/${C.dir}/${f.file}`)).then((r) => r.arrayBuffer())));
        const sec = { id, ...(await exactSector(q, ins)), lit: await litBytes(q, ins[1]) };
        const H = rasterHeights(gmChunks(ring.relief(id)), q.rect, w, h);
        const ref = ringPaint(G.world, sec, level, H);
        const px = new Uint8Array(4 * w * h);
        gl.bindFramebuffer(gl.FRAMEBUFFER, A.target(e).framebuffer);
        gl.readPixels(e.inner[0], e.inner[1], w, h, gl.RGBA, gl.UNSIGNED_BYTE, px);
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        R.resetState();
        let maxd = 0, over1 = 0;
        for (let k = 0; k < w * h; k++) {
          for (let c = 0; c < 3; c++) {
            const d = Math.abs(px[4 * k + c] - ref.rgba[4 * k + c]);
            if (d > maxd) maxd = d;
            if (d > 1) over1++;
          }
        }
        out.push({ id, level, maxd, over1 });
      }
      return { sectors: out, ok: out.length > 0 && out.every((s) => s.maxd <= 1) };
    };
  }
  return pass;
}
